import init, { fit_pipeline_vec, fit_manual_vec } from './pkg/svg_fit.js';
import type { StashItem, MultiGroupItem, ImageCoords } from './types.js';
import { DEFAULTS } from './types.js';
import {
  $, $input, fileInput, mainCanvas, mainCtx, mainSvg, mainPanel, mainContent,
  seedMarker, placeholder, statusBar, exportBtn, copyBtn, stashBtn, stashBar,
  stashItems, clearStashBtn, refitBtn, semiManualBtn,
  geneticBar, gaFitBtn, paintBtn, brushSizeEl, brushSizeVal,
  pickColorBtn, paintModeBtn, eraseModeBtn, resetPaintBtn, paintCursorSvg,
  paintCanvas, modeBtn, toast, sliders, gaSliders, nodesSvg,
} from './dom.js';
import { saveState, loadState, persistImage, restoreImage, saveFileStash, loadFileStash } from './persist.js';
import { ZoomPan } from './zoompan.js';
import {
  paintOpen, paintMode, paintColor, pickingColor, originalRgba,
  paintDragging, paintFitTimer, lastPaintX, lastPaintY,
  updateMainImg, enterPaintMode, exitPaintMode, setPaintColor,
  resetPainting, applyPaintCircle, applyPaintLine,
  updatePaintCursor, hidePaintCursor,
  setPaintDragging, setLastPaint, setPaintFitTimer, setPickingColor, setPaintMode,
} from './paint.js';
import {
  semiManual, smNodes,
  enterSemiManual, exitSemiManual, handleSemiManualClick, renderNodesForZoom,
} from './semi-manual.js';
import {
  autoGASession, autoGARunning, gaSessionId, gaRunning,
  cancelAutoGA, cancelManualGA, runAutoGA, runManualGA,
} from './ga.js';
import { sampleColor, buildStashSvg, renderStash, showToast } from './stash.js';
import { initKnobs } from './knob.js';

// -- WASM init --
const wasmReady = init();

// -- App state --
let rgba: Uint8ClampedArray | null = null;
let imgW = 0;
let imgH = 0;
let seedX: number | null = null;
let seedY: number | null = null;
let svgPath: string | null = null;
let overlayMode = true;
let fitTimer: ReturnType<typeof setTimeout> | null = null;
let stash: StashItem[] = [];
let stashCounter = 0;
let currentFileKey: string | null = null;
let multiGroup: MultiGroupItem[] = [];
let dragSeedPlaced = false;

let smFitTimer: ReturnType<typeof setTimeout> | null = null;

// -- Wrapped state save (captures app-level state) --
function appSaveState(): void {
  saveState({
    seedX, seedY, stash, stashCounter, sliders, gaSliders,
    overlayMode,
    overlayColor: ($('overlayColor') as HTMLInputElement).value,
    noHoles: ($('noHoles') as HTMLInputElement).checked,
    shapeHint: ($('shapeHint') as HTMLSelectElement).value,
  });
}

function appSaveFileStash(): void {
  saveFileStash(currentFileKey, stash);
}

// -- Coord helpers --
function imageCoords(e: MouseEvent): ImageCoords {
  const r = mainPanel.getBoundingClientRect();
  return {
    x: (e.clientX - r.left - zp.panX) / zp.zoom,
    y: (e.clientY - r.top - zp.panY) / zp.zoom,
  };
}

// -- ZoomPan --
const zp = new ZoomPan(mainPanel, mainContent);

// -- Preview --
function updatePreview(): void {
  if (!rgba) { placeholder.style.display = 'flex'; return; }

  const hasStashVisible = stash.some(i => i.visible);
  if (!svgPath && !hasStashVisible && multiGroup.length === 0) {
    mainSvg.style.display = 'none';
    return;
  }

  mainSvg.style.display = 'block';
  mainSvg.setAttribute('viewBox', `0 0 ${imgW} ${imgH}`);
  mainSvg.setAttribute('width', String(imgW));
  mainSvg.setAttribute('height', String(imgH));

  const color = ($('overlayColor') as HTMLInputElement).value;
  const opacity = sliders.opacity.el.value;

  let paths = '';
  for (const item of stash) {
    if (!item.visible) continue;
    if (item.groupPaths) {
      for (const p of item.groupPaths) {
        if (svgPath && p.path === svgPath) continue;
        paths += `<path d="${p.path}" fill="${item.color}" fill-opacity="${opacity}" stroke="${item.color}" stroke-width="1" fill-rule="evenodd"/>`;
      }
    } else {
      if (svgPath && item.path === svgPath) continue;
      paths += `<path d="${item.path}" fill="${item.color}" fill-opacity="${opacity}" stroke="${item.color}" stroke-width="1" fill-rule="evenodd"/>`;
    }
  }
  for (const g of multiGroup) {
    paths += `<path d="${g.path}" fill="${color}" fill-opacity="${opacity}" stroke="${color}" stroke-width="1" fill-rule="evenodd"/>`;
  }
  if (svgPath) {
    paths += `<path d="${svgPath}" fill="${color}" fill-opacity="${opacity}" stroke="${color}" stroke-width="1" fill-rule="evenodd"/>`;
  }
  mainSvg.innerHTML = paths;

  if (overlayMode) {
    mainCanvas.style.display = 'block';
    mainPanel.classList.remove('checkerboard');
  } else {
    mainCanvas.style.display = 'none';
    mainPanel.classList.add('checkerboard');
  }
}

// -- Fit pipeline --
function scheduleFit(): void {
  if (!rgba || seedX === null) return;
  if (fitTimer) clearTimeout(fitTimer);
  cancelAutoGA();
  if (($('autoGA') as HTMLInputElement).checked) {
    fitTimer = setTimeout(runAutoFit, 50);
  } else {
    fitTimer = setTimeout(runFit, 50);
  }
}

async function runAutoFit(): Promise<void> {
  if (!rgba || seedX === null) return;
  await runAutoGA(
    rgba, imgW, imgH, seedX!, seedY!,
    parseFloat(sliders.tolerance.el.value),
    parseInt(sliders.margin.el.value),
    ($('noHoles') as HTMLInputElement).checked,
    wasmReady,
    (path: string) => {
      svgPath = path;
      updatePreview();
      stashBtn.disabled = false;
      exportBtn.disabled = copyBtn.disabled = refitBtn.disabled = semiManualBtn.disabled = paintBtn.disabled = false;
    },
    (msg: string) => { statusBar.textContent = 'Error: ' + msg; },
  );
}

async function runFit(): Promise<void> {
  await wasmReady;
  statusBar.textContent = 'Fitting...';
  const t0 = performance.now();
  try {
    const seg = parseInt(sliders.segments.el.value) >= 161 ? 0 : parseInt(sliders.segments.el.value);
    svgPath = fit_pipeline_vec(
      rgba!, imgW, imgH,
      seedX!, seedY!,
      parseFloat(sliders.tolerance.el.value),
      parseInt(sliders.margin.el.value),
      parseFloat(sliders.rdp.el.value),
      seg,
      parseInt(($('shapeHint') as HTMLSelectElement).value),
      ($('noHoles') as HTMLInputElement).checked,
    );
    const dt = (performance.now() - t0).toFixed(1);
    updatePreview();
    stashBtn.disabled = false;
    exportBtn.disabled = copyBtn.disabled = refitBtn.disabled = semiManualBtn.disabled = paintBtn.disabled = false;
    const n = multiGroup.length + 1;
    statusBar.textContent = `Fitted in ${dt}ms` + (multiGroup.length > 0 ? ` (${n} selected)` : '');
  } catch (e: unknown) {
    statusBar.textContent = 'Error: ' + (e instanceof Error ? e.message : String(e));
    console.error(e);
  }
}

async function runSmFit(): Promise<void> {
  if (!semiManual || smNodes.length < 3 || seedX === null || !rgba) return;
  await wasmReady;
  statusBar.textContent = 'Fitting manual...';
  const t0 = performance.now();
  try {
    const anchors = new Float32Array(smNodes.length * 2);
    const smoothFlags = new Uint8Array(smNodes.length);
    smNodes.forEach((n, i) => {
      anchors[i * 2] = n.x;
      anchors[i * 2 + 1] = n.y;
      smoothFlags[i] = n.type === 'smooth' ? 1 : 0;
    });
    svgPath = fit_manual_vec(
      rgba, imgW, imgH,
      seedX!, seedY!,
      parseFloat(sliders.tolerance.el.value),
      parseInt(sliders.margin.el.value),
      anchors,
      smoothFlags,
    );
    const dt = (performance.now() - t0).toFixed(1);
    updatePreview();
    stashBtn.disabled = false;
    exportBtn.disabled = copyBtn.disabled = refitBtn.disabled = false;
    statusBar.textContent = `Manual fit in ${dt}ms (${smNodes.length} nodes)`;
  } catch (e: unknown) {
    statusBar.textContent = 'Error: ' + (e instanceof Error ? e.message : String(e));
    console.error(e);
  }
}

function scheduleSmFit(): void {
  if (!semiManual || smNodes.length < 3) return;
  if (smFitTimer) clearTimeout(smFitTimer);
  smFitTimer = setTimeout(runSmFit, 50);
}

// -- Seed helpers --
function renderMultiMarkers(): void {
  mainContent.querySelectorAll('.multi-seed-marker').forEach(el => el.remove());
  for (let i = 0; i < multiGroup.length; i++) {
    const m = multiGroup[i];
    const el = document.createElement('div');
    el.className = 'seed-crosshair multi-seed-marker';
    el.style.display = 'block';
    el.style.left = m.seedX + 'px';
    el.style.top = m.seedY + 'px';
    el.textContent = String(i + 1);
    el.style.color = '#60a5fa';
    mainContent.appendChild(el);
  }
}

function clearMultiGroup(): void {
  multiGroup = [];
  mainContent.querySelectorAll('.multi-seed-marker').forEach(el => el.remove());
}

function placeSeed(sx: number, sy: number): void {
  seedX = sx; seedY = sy;
  seedMarker.style.display = 'block';
  seedMarker.style.left = seedX + 'px';
  seedMarker.style.top = seedY + 'px';
}

// -- ZoomPan event handlers --
zp.onDown = (e: MouseEvent) => {
  if (!rgba) return;
  const { x, y } = imageCoords(e);
  if (x < 0 || y < 0 || x >= imgW || y >= imgH) return;

  if (paintOpen) {
    if (e.button === 0 && !pickingColor) {
      setPaintDragging(true);
      setLastPaint(x, y);
      applyPaintCircle(rgba, imgW, imgH, x, y);
      updateMainImg(rgba, imgW, imgH, mainCtx);
    }
    return;
  }

  if (semiManual) return;
  cancelAutoGA();
  const sx = Math.round(x), sy = Math.round(y);
  if (seedX !== null && seedY !== null) {
    const dx = sx - seedX, dy = sy - seedY;
    if (dx * dx + dy * dy < 25) return;
  }
  if (e.shiftKey && svgPath) {
    multiGroup.push({
      path: svgPath,
      seedX: seedX!, seedY: seedY!,
      sourceColor: sampleColor(rgba, imgW, seedX!, seedY!),
    });
    renderMultiMarkers();
  } else if (!e.shiftKey) {
    clearMultiGroup();
  }
  placeSeed(sx, sy);
  dragSeedPlaced = true;
};

zp.onClick = (e: MouseEvent) => {
  if (!rgba) return;
  const { x, y } = imageCoords(e);
  if (x < 0 || y < 0 || x >= imgW || y >= imgH) return;

  if (paintOpen && pickingColor) {
    const ix = Math.round(x), iy = Math.round(y);
    const i = (iy * imgW + ix) * 4;
    if (rgba[i + 3] > 0) {
      setPaintColor(rgba[i], rgba[i + 1], rgba[i + 2]);
    }
    setPickingColor(false);
    pickColorBtn.textContent = 'Pick';
    mainPanel.style.cursor = 'none';
    paintCursorSvg.style.display = 'block';
    statusBar.textContent = 'Paint mode: draw to bridge regions.';
    return;
  }

  if (paintOpen) return;

  if (semiManual) {
    handleSemiManualClick(x, y, zp);
    scheduleSmFit();
    return;
  }

  const sx = Math.round(x), sy = Math.round(y);

  if (dragSeedPlaced) {
    dragSeedPlaced = false;
  } else if (seedX !== null && seedY !== null) {
    const dx = sx - seedX, dy = sy - seedY;
    if (dx * dx + dy * dy < 25) {
      seedX = seedY = null;
      svgPath = null;
      seedMarker.style.display = 'none';
      clearMultiGroup();
      stashBtn.disabled = true;
      refitBtn.disabled = semiManualBtn.disabled = true;
      exportBtn.disabled = copyBtn.disabled = !stash.some(i => i.include !== false);
      updatePreview();
      appSaveState();
      statusBar.textContent = '';
      return;
    }
  }

  appSaveState();
  scheduleFit();
};

zp.onDrag = (screenDist: number) => {
  if (!rgba || seedX === null || semiManual || paintOpen) return;
  const tol = Math.min(100, Math.max(0, Math.round(screenDist / zp.zoom)));
  sliders.tolerance.el.value = String(tol);
  sliders.tolerance.val.textContent = sliders.tolerance.fmt(String(tol));
  scheduleFit();
};

zp.onDragEnd = () => {
  dragSeedPlaced = false;
  appSaveState();
};

// -- Paint cursor tracking + stroke --
mainPanel.addEventListener('mousemove', (e: MouseEvent) => {
  if (!paintOpen || !rgba) return;
  const { x, y } = imageCoords(e);

  updatePaintCursor(imgW, imgH, x, y);

  if (paintDragging && !pickingColor) {
    if (lastPaintX !== null) {
      applyPaintLine(rgba, imgW, imgH, lastPaintX!, lastPaintY!, x, y);
      updateMainImg(rgba, imgW, imgH, mainCtx);
    }
    setLastPaint(x, y);
  }
});

mainPanel.addEventListener('mouseleave', () => {
  if (paintOpen) hidePaintCursor();
});

window.addEventListener('mouseup', (e: MouseEvent) => {
  if (paintDragging && e.button === 0) {
    setPaintDragging(false);
    setLastPaint(null, null);
    updateMainImg(rgba!, imgW, imgH, mainCtx);
    if (paintFitTimer) clearTimeout(paintFitTimer);
    setPaintFitTimer(setTimeout(scheduleFit, 1000));
  }
});

// -- Slider setup --
for (const [name, s] of Object.entries(sliders)) {
  s.el.oninput = () => {
    s.val.textContent = s.fmt(s.el.value);
    if (name === 'opacity') { updatePreview(); }
    else if (semiManual) { scheduleSmFit(); }
    else { scheduleFit(); }
    appSaveState();
  };
}

($input('overlayColor') as HTMLInputElement).oninput = () => { updatePreview(); appSaveState(); };
($('randomColorBtn') as HTMLElement).onclick = () => {
  const h = '#' + Array.from({ length: 3 }, () => Math.floor(Math.random() * 128 + 80).toString(16).padStart(2, '0')).join('');
  ($input('overlayColor') as HTMLInputElement).value = h;
  updatePreview(); appSaveState();
};
($('shapeHint') as HTMLSelectElement).onchange = () => { if (semiManual) scheduleSmFit(); else scheduleFit(); appSaveState(); };
($input('noHoles') as HTMLInputElement).onchange = () => { if (semiManual) scheduleSmFit(); else scheduleFit(); appSaveState(); };

for (const s of Object.values(gaSliders)) {
  s.el.oninput = () => { s.val.textContent = s.fmt(s.el.value); scheduleFit(); appSaveState(); };
}

brushSizeEl.oninput = () => { brushSizeVal.textContent = brushSizeEl.value; };

// -- +/- adjuster buttons --
document.querySelectorAll('.adj').forEach(btn => {
  btn.addEventListener('click', (e: Event) => {
    e.stopPropagation();
    const targetId = (btn as HTMLElement).dataset.target!;
    const dir = parseInt((btn as HTMLElement).dataset.dir!);
    const input = $input(targetId);
    const step = parseFloat(input.step) || 1;
    const newVal = Math.max(parseFloat(input.min), Math.min(parseFloat(input.max), parseFloat(input.value) + step * dir));
    input.value = String(newVal);
    input.dispatchEvent(new Event('input'));
  });
});

// -- Mode toggle --
modeBtn.onclick = () => {
  overlayMode = !overlayMode;
  modeBtn.textContent = overlayMode ? 'Overlay' : 'Blob Only';
  updatePreview();
  appSaveState();
};

// -- Paint UI wiring --
paintBtn.onchange = () => {
  if (paintBtn.checked) {
    enterPaintMode(rgba!, imgW, imgH);
    paintBar.classList.remove('section-muted');
  } else {
    exitPaintMode();
    paintBar.classList.add('section-muted');
  }
};

resetPaintBtn.onclick = () => {
  resetPainting(rgba!, imgW, imgH, mainCtx);
  scheduleFit();
  statusBar.textContent = 'Painting reset to original image.';
};

pickColorBtn.onclick = () => {
  const next = !pickingColor;
  setPickingColor(next);
  pickColorBtn.textContent = next ? 'Cancel' : 'Pick';
  if (next) {
    mainPanel.style.cursor = 'crosshair';
    paintCursorSvg.style.display = 'none';
    statusBar.textContent = 'Click image to pick a color...';
  } else {
    mainPanel.style.cursor = 'none';
    statusBar.textContent = 'Paint: draw to bridge regions.';
  }
};

paintModeBtn.onclick = () => {
  setPaintMode('paint');
};

eraseModeBtn.onclick = () => {
  setPaintMode('erase');
};

// -- Semi-manual UI --
semiManualBtn.onchange = () => {
  if (semiManualBtn.checked) enterSemiManual();
  else exitSemiManual();
};

// -- Genetic UI --


gaFitBtn.onclick = () => {
  runManualGA(
    rgba!, imgW, imgH, seedX!, seedY!,
    parseFloat(sliders.tolerance.el.value),
    parseInt(sliders.margin.el.value),
    ($('noHoles') as HTMLInputElement).checked,
    wasmReady,
    (msg: string) => { statusBar.textContent = msg; },
    (path: string) => {
      svgPath = path;
      updatePreview();
      stashBtn.disabled = false;
      exportBtn.disabled = copyBtn.disabled = refitBtn.disabled = semiManualBtn.disabled = paintBtn.disabled = false;
    },
    (msg: string) => { statusBar.textContent = 'Error: ' + msg; },
    gaFitBtn,
  );
};

// -- Refit / Fit (swaps based on semi-manual mode) --
refitBtn.onclick = () => {
  if (semiManual) { runSmFit(); return; }
  if (!svgPath && (seedX === null || !rgba)) return;
  runFit();
};

// -- Stash --
function addToStash(name: string): void {
  if (!svgPath && multiGroup.length === 0) return;
  stashCounter++;
  const hasGroup = multiGroup.length > 0;
  const item: StashItem = {
    id: '#' + stashCounter,
    name: name || '',
    path: svgPath,
    color: ($('overlayColor') as HTMLInputElement).value,
    sourceColor: rgba ? sampleColor(rgba, imgW, seedX!, seedY!) : '#66ccff',
    include: true,
    visible: true,
  };
  if (hasGroup) {
    item.groupPaths = [
      ...multiGroup.map(g => ({ path: g.path, sourceColor: g.sourceColor })),
      { path: svgPath!, sourceColor: rgba ? sampleColor(rgba, imgW, seedX!, seedY!) : '#66ccff' },
    ];
  }
  stash.unshift(item);
  if (hasGroup) clearMultiGroup();
  exportBtn.disabled = copyBtn.disabled = false;
  renderStash(stash, updatePreview, appSaveState, appSaveFileStash);
  updatePreview();
  appSaveState();
  appSaveFileStash();
  showToast(hasGroup ? 'Group added to stash' : 'Added to stash');
}

function promptStashName(): void {
  if (!svgPath && multiGroup.length === 0) return;
  ($('stashNameModal') as HTMLElement).style.display = 'flex';
  const isGroup = multiGroup.length > 0;
  ($('stashModalTitle') as HTMLElement).textContent = isGroup ? `Name this group (${multiGroup.length + 1} shapes)` : 'Name this shape';
  const input = $input('stashNameInput');
  input.value = '';
  input.focus();
}

function closeStashNameModal(): void {
  ($('stashNameModal') as HTMLElement).style.display = 'none';
}

function confirmStashName(): void {
  const name = ($input('stashNameInput')).value.trim();
  closeStashNameModal();
  addToStash(name);
}

($('stashNameOk') as HTMLElement).onclick = confirmStashName;
($('stashNameCancel') as HTMLElement).onclick = closeStashNameModal;
$input('stashNameInput').onkeydown = (e: KeyboardEvent) => {
  if (e.key === 'Enter') { e.preventDefault(); confirmStashName(); }
  if (e.key === 'Escape') closeStashNameModal();
};
($('stashNameModal') as HTMLElement).onclick = (e: MouseEvent) => {
  if (e.target === $('stashNameModal')) closeStashNameModal();
};

stashBtn.onclick = promptStashName;

document.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Enter' && !(e.target as HTMLElement).closest('input, select, textarea, #stashNameModal, #helpModal')) {
    e.preventDefault();
    promptStashName();
  }
});

clearStashBtn.onclick = () => {
  stash = [];
  exportBtn.disabled = copyBtn.disabled = true;
  renderStash(stash, updatePreview, appSaveState, appSaveFileStash);
  updatePreview();
  appSaveState();
  appSaveFileStash();
};

// -- Export / Copy --
exportBtn.onclick = () => {
  const svg = buildStashSvg(stash, imgW, imgH);
  const blob = new Blob([svg], { type: 'image/svg+xml' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'blob.svg';
  a.click();
  URL.revokeObjectURL(a.href);
  showToast('SVG exported');
};

copyBtn.onclick = () => {
  const svg = buildStashSvg(stash, imgW, imgH);
  navigator.clipboard.writeText(svg).then(() => showToast('SVG copied'));
};

// -- File upload --
fileInput.onchange = async () => {
  const file = fileInput.files?.[0];
  if (!file) return;

  const bitmap = await createImageBitmap(file);
  imgW = bitmap.width; imgH = bitmap.height;

  const canvas = document.createElement('canvas');
  canvas.width = imgW; canvas.height = imgH;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0);
  const imageData = ctx.getImageData(0, 0, imgW, imgH);
  rgba = imageData.data as Uint8ClampedArray;

  bitmap.close();

  appSaveFileStash();

  const newFileKey = file.name + '-' + file.size;
  currentFileKey = newFileKey;

  await persistImage(file, rgba, imgW, imgH);

  mainCanvas.width = imgW;
  mainCanvas.height = imgH;
  mainCanvas.style.display = 'block';
  mainCtx.putImageData(new ImageData(new Uint8ClampedArray(rgba), imgW, imgH), 0, 0);
  placeholder.style.display = 'none';
  seedMarker.style.display = 'none';

  requestAnimationFrame(() => zp.fitToPanel(imgW, imgH));

  seedX = seedY = null; svgPath = null;
  clearMultiGroup();
  stash = []; stashCounter = 0;
  stashBtn.disabled = true;
  exportBtn.disabled = copyBtn.disabled = refitBtn.disabled = semiManualBtn.disabled = true;
  paintBtn.disabled = false;
  if (semiManual) exitSemiManual();
  if (paintOpen) exitPaintMode();
  if (gaRunning) { cancelManualGA(); gaFitBtn.textContent = 'Evolve & Fit'; }
  renderStash(stash, updatePreview, appSaveState, appSaveFileStash);
  updatePreview();

  const savedStash = loadFileStash(newFileKey);
  if (savedStash && savedStash.length > 0) {
    stash = savedStash as StashItem[];
    stashCounter = stash.reduce((mx, s) => {
      const n = parseInt(s.id.replace(/\D/g, '')) || 0;
      return Math.max(mx, n);
    }, 0);
    exportBtn.disabled = copyBtn.disabled = !stash.some(i => i.include !== false);
    renderStash(stash, updatePreview, appSaveState, appSaveFileStash);
    updatePreview();
  }

  statusBar.textContent = `Image: ${imgW}\u00d7${imgH}`;
  fileInput.value = '';
  appSaveState();
};

// -- Demo --
async function loadDemo(): Promise<void> {
  try {
    const resp = await fetch('demo.png');
    if (!resp.ok) return;
    const blob = await resp.blob();
    const file = new File([blob], 'demo.png', { type: blob.type });
    const bitmap = await createImageBitmap(blob);
    imgW = bitmap.width; imgH = bitmap.height;
    const canvas = document.createElement('canvas');
    canvas.width = imgW; canvas.height = imgH;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bitmap, 0, 0);
    rgba = ctx.getImageData(0, 0, imgW, imgH).data as Uint8ClampedArray;
    bitmap.close();

    currentFileKey = '__demo__';
    mainCanvas.width = imgW;
    mainCanvas.height = imgH;
    mainCanvas.style.display = 'block';
    mainCtx.putImageData(new ImageData(new Uint8ClampedArray(rgba), imgW, imgH), 0, 0);
    placeholder.style.display = 'none';
    requestAnimationFrame(() => zp.fitToPanel(imgW, imgH));
    statusBar.textContent = 'Demo \u2014 click a region to try it, or load your own image';
  } catch (e) { console.warn('Demo load failed:', e); }
}

// -- Restore session --
async function restoreSession(): Promise<void> {
  const saved = loadState();
  if (!saved) { await loadDemo(); return; }

  if (saved.tolerance != null) { sliders.tolerance.el.value = saved.tolerance; sliders.tolerance.val.textContent = sliders.tolerance.fmt(saved.tolerance); }
  if (saved.margin != null) { sliders.margin.el.value = saved.margin; sliders.margin.val.textContent = sliders.margin.fmt(saved.margin); }
  if (saved.rdp != null) { sliders.rdp.el.value = saved.rdp; sliders.rdp.val.textContent = sliders.rdp.fmt(saved.rdp); }
  if (saved.segments != null) { sliders.segments.el.value = saved.segments; sliders.segments.val.textContent = sliders.segments.fmt(saved.segments); }
  if (saved.opacity != null) { sliders.opacity.el.value = saved.opacity; sliders.opacity.val.textContent = sliders.opacity.fmt(saved.opacity); }
  if (saved.overlayColor) { ($('overlayColor') as HTMLInputElement).value = saved.overlayColor; }
  if (saved.shapeHint != null) { ($('shapeHint') as HTMLSelectElement).value = saved.shapeHint; }
  if (saved.noHoles != null) { ($('noHoles') as HTMLInputElement).checked = saved.noHoles; }
  if (saved.gaPenalty != null) { gaSliders.penalty.el.value = saved.gaPenalty; gaSliders.penalty.val.textContent = gaSliders.penalty.fmt(saved.gaPenalty); }
  if (saved.gaOvershoot != null) { gaSliders.overshoot.el.value = saved.gaOvershoot; gaSliders.overshoot.val.textContent = gaSliders.overshoot.fmt(saved.gaOvershoot); }
  if (saved.gaPop != null) { gaSliders.pop.el.value = saved.gaPop; gaSliders.pop.val.textContent = gaSliders.pop.fmt(saved.gaPop); }
  if (saved.gaGen != null) { gaSliders.gen.el.value = saved.gaGen; gaSliders.gen.val.textContent = gaSliders.gen.fmt(saved.gaGen); }
  if (saved.overlayMode != null) {
    overlayMode = saved.overlayMode;
    modeBtn.textContent = overlayMode ? 'Overlay' : 'Blob Only';
  }

  const img = await restoreImage();
  if (!img) return;

  rgba = img.rgba; imgW = img.imgW; imgH = img.imgH;

  currentFileKey = img.file.name + '-' + img.file.size;

  mainCanvas.width = imgW;
  mainCanvas.height = imgH;
  mainCanvas.style.display = 'block';
  mainCtx.putImageData(new ImageData(new Uint8ClampedArray(rgba), imgW, imgH), 0, 0);
  placeholder.style.display = 'none';

  requestAnimationFrame(() => zp.fitToPanel(imgW, imgH));

  statusBar.textContent = `Image: ${imgW}\u00d7${imgH}`;
  paintBtn.disabled = false;

  if (saved.stash) {
    stash = saved.stash;
    stashCounter = saved.stashCounter || stash.length;
    exportBtn.disabled = copyBtn.disabled = !stash.some(i => i.include !== false);
    renderStash(stash, updatePreview, appSaveState, appSaveFileStash);
  }

  if (saved.seedX != null && saved.seedY != null) {
    seedX = saved.seedX; seedY = saved.seedY;
    seedMarker.style.display = 'block';
    seedMarker.style.left = seedX + 'px';
    seedMarker.style.top = seedY + 'px';
    scheduleFit();
  }

  updatePreview();
}

restoreSession();

// -- Init knobs (replaces range inputs with rotary knobs) --
initKnobs(sliders, gaSliders, [{ cfg: { el: brushSizeEl, val: brushSizeVal, fmt: (v: string) => v }, label: 'Brush Size' }]);

// -- Help Modal --
($('helpBtn') as HTMLElement).onclick = () => { ($('helpModal') as HTMLElement).style.display = 'flex'; };
($('helpClose') as HTMLElement).onclick = () => { ($('helpModal') as HTMLElement).style.display = 'none'; };
($('helpModal') as HTMLElement).onclick = (e: MouseEvent) => {
  if (e.target === $('helpModal')) ($('helpModal') as HTMLElement).style.display = 'none';
};
document.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    const anyModalOpen = ($('helpModal') as HTMLElement).style.display !== 'none' || ($('stashNameModal') as HTMLElement).style.display !== 'none';
    ($('helpModal') as HTMLElement).style.display = 'none';
    if (!anyModalOpen && multiGroup.length > 0) {
      clearMultiGroup();
      updatePreview();
      statusBar.textContent = 'Group selection cancelled';
    }
  }
});

// -- Settings reset --
($('resetSettingsBtn') as HTMLElement).onclick = () => {
  sliders.tolerance.el.value = String(DEFAULTS.tolerance); sliders.tolerance.val.textContent = sliders.tolerance.fmt(String(DEFAULTS.tolerance));
  sliders.margin.el.value = String(DEFAULTS.margin); sliders.margin.val.textContent = sliders.margin.fmt(String(DEFAULTS.margin));
  sliders.rdp.el.value = String(DEFAULTS.rdpEpsilon); sliders.rdp.val.textContent = sliders.rdp.fmt(String(DEFAULTS.rdpEpsilon));
  sliders.segments.el.value = String(DEFAULTS.segments); sliders.segments.val.textContent = sliders.segments.fmt(String(DEFAULTS.segments));
  sliders.opacity.el.value = String(DEFAULTS.overlayOpacity); sliders.opacity.val.textContent = sliders.opacity.fmt(String(DEFAULTS.overlayOpacity));
  ($('overlayColor') as HTMLInputElement).value = DEFAULTS.overlayColor;
  ($('shapeHint') as HTMLSelectElement).value = DEFAULTS.shapeHint;
  ($('noHoles') as HTMLInputElement).checked = DEFAULTS.noHoles;
  gaSliders.penalty.el.value = String(DEFAULTS.gaPenalty); gaSliders.penalty.val.textContent = gaSliders.penalty.fmt(String(DEFAULTS.gaPenalty));
  gaSliders.overshoot.el.value = String(DEFAULTS.gaOvershoot); gaSliders.overshoot.val.textContent = gaSliders.overshoot.fmt(String(DEFAULTS.gaOvershoot));
  gaSliders.pop.el.value = String(DEFAULTS.gaPop); gaSliders.pop.val.textContent = gaSliders.pop.fmt(String(DEFAULTS.gaPop));
  gaSliders.gen.el.value = String(DEFAULTS.gaGen); gaSliders.gen.val.textContent = gaSliders.gen.fmt(String(DEFAULTS.gaGen));
  scheduleFit();
  updatePreview();
  appSaveState();
  showToast('Settings reset to defaults');
};
